import React from 'react';
import { Box, Typography, Paper, useTheme } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

/**
 * Компонент для отображения видео контента
 * @param {Object} options - Объект с данными видео (videoUrl, description)
 * @param {boolean} withMargin - Добавлять ли отступ снизу (по умолчанию true)
 */
const VideoContent = ({ options, withMargin = true }) => {
  const theme = useTheme();

  // Проверка на наличие данных
  if (!options || !options.videoUrl) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          textAlign: 'center',
          bgcolor: theme.palette.background.paper,
          color: theme.palette.text.secondary,
          borderRadius: '16px',
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <PlayCircleOutlineIcon fontSize="large" />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Видео недоступно или не было добавлено
        </Typography>
      </Paper>
    );
  }

  // Функция для проверки, указывает ли URL на прямой видеофайл
  const isDirectVideoFile = (url) => {
    if (!url) return false;
    const videoExtensions = /\.(mp4|webm|ogg|mov|avi|wmv|flv|mkv)(\?.*)?$/i;
    return videoExtensions.test(url);
  };

  // Функция для определения MIME типа видео по расширению
  const getVideoMimeType = (url) => {
    if (!url) return 'video/mp4';
    const extension = url.match(/\.([^.?]+)(\?.*)?$/i);
    if (!extension) return 'video/mp4';

    const mimeTypes = {
      mp4: 'video/mp4',
      webm: 'video/webm',
      ogg: 'video/ogg',
      mov: 'video/quicktime',
      avi: 'video/x-msvideo',
      wmv: 'video/x-ms-wmv',
      flv: 'video/x-flv',
      mkv: 'video/x-matroska',
    };

    return mimeTypes[extension[1].toLowerCase()] || 'video/mp4';
  };

  // Функция для получения встраиваемого URL на основе обычного URL
  const getEmbedUrl = (url) => {
    if (!url) return '';

    // YouTube
    const youtubeRegex = new RegExp(
      '(?:youtube\\.com/(?:[^/]+/.+/|(?:v|e(?:mbed)?)/|.*[?&]v=)|youtu\\.be/)([^"&?/\\s]{11})',
      'i'
    );
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoRegex = new RegExp('(?:vimeo\\.com/)(\\d+)', 'i');
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    // Если не удалось преобразовать URL, возвращаем исходный
    return url;
  };

  // Получаем встраиваемый URL
  const embedUrl = getEmbedUrl(options.videoUrl);
  const isVideo = isDirectVideoFile(options.videoUrl);

  return (
    <Box sx={{ width: '100%', ...(withMargin && { mb: 4 }) }}>
      {/* Видео плеер */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          pb: '56.25%',
          height: 0,
          mb: options.description ? 3 : 0,
        }}
      >
        {isVideo ? (
          <video
            controls
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: '16px',
              backgroundColor: '#000',
            }}
          >
            <source src={options.videoUrl} type={getVideoMimeType(options.videoUrl)} />
            Ваш браузер не підтримує відео тег.
          </video>
        ) : (
          <iframe
            src={embedUrl}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: '16px',
            }}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Video Content"
          />
        )}
      </Box>
    </Box>
  );
};

export default VideoContent;
